/**
 * ImpersonateCallback.js
 * Componente que maneja el callback de impersonation.
 * Valida el token temporal y establece la sesión como Admin del colegio.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { colegiosService } from '../../services/colegiosService';
import '../../App.css';

function ImpersonateCallback({ onLoginSuccess }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState('validando'); // validando, exito, error
  const [mensaje, setMensaje] = useState('Validando acceso...');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const validarToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setEstado('error');
        setMensaje('Token no proporcionado. Esta URL no es valida.');
        return;
      }

      try {
        const response = await colegiosService.validarImpersonation(token);

        if (response.success) {
          // IMPORTANTE: Usar sessionStorage para sesiones de impersonation
          // Esto evita sobrescribir la sesión del Root en localStorage (compartido entre pestañas)
          // sessionStorage es específico de cada pestaña/ventana
          sessionStorage.setItem('token', response.token);
          sessionStorage.setItem('user', JSON.stringify(response.user));
          sessionStorage.setItem('isImpersonated', 'true');

          setUserData(response.user);
          setEstado('exito');
          setMensaje(response.user.tipoUsuario === 'SISTEMA'
            ? `Acceso exitoso como ${response.user.role}`
            : `Acceso exitoso a ${response.user.colegioNombre}`);

          // Notificar al App.js sobre el login exitoso
          if (onLoginSuccess) {
            onLoginSuccess(response.user);
          }

          // Redirigir al dashboard después de 1.5 segundos
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          setEstado('error');
          setMensaje(response.message || 'Error al validar el acceso');
        }
      } catch (error) {
        console.error('Error en impersonation callback:', error);
        setEstado('error');
        setMensaje(error.message || 'Token invalido o expirado. Por favor intente nuevamente.');
      }
    };

    validarToken();
  }, [searchParams, navigate, onLoginSuccess]);

  return (
    <div className="login-page">
      <div className="login-box impersonate-callback">
        <div className="login-header">
          <h1>Academik</h1>
          <p>Acceso Administrativo</p>
        </div>

        <div className="impersonate-status">
          {estado === 'validando' && (
            <div className="status-validando">
              <div className="spinner"></div>
              <p>{mensaje}</p>
            </div>
          )}

          {estado === 'exito' && userData && (
            <div className="status-exito">
              <div className="status-icon">✓</div>
              <h3>{mensaje}</h3>
              <div className="impersonate-info">
                <p><strong>Usuario:</strong> {userData.fullName}</p>
                <p><strong>Rol:</strong> {userData.role}</p>
                {userData.colegioNombre && <p><strong>Colegio:</strong> {userData.colegioNombre}</p>}
                {userData.colegioAnio && <p><strong>Año:</strong> {userData.colegioAnio}</p>}
              </div>
              {userData.isImpersonated && (
                <div className="impersonate-warning">
                  Sesion iniciada por: <strong>{userData.impersonatedBy}</strong>
                </div>
              )}
              <p className="redirect-msg">Redirigiendo al dashboard...</p>
            </div>
          )}

          {estado === 'error' && (
            <div className="status-error">
              <div className="status-icon error">✕</div>
              <h3>Error de Acceso</h3>
              <p>{mensaje}</p>
              <button
                className="btn-retry"
                onClick={() => window.close()}
              >
                Cerrar esta ventana
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImpersonateCallback;
